import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation, Trans } from "react-i18next";

import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

import "pdfjs-dist";

import {
    Box,
    Button,
    Input,
    Menu,
    MenuItem,
    Paper,
    useTheme,
} from "@mui/material";

import store from "../../../../store/index";
import {
    setStoreData,
    setIsSceneUpdated,
    setSelectedStoreElement,
    setIsFloorEdit,
    setRecenterMap,
    showProjectElements,
    setZoomToElementId
} from "../../../../store/actions/Store";
import { toggleMessageOpen } from "../../../../store/actions/Message";

import { IS_DEV } from "../../../../isdev";

import DXF, { DxfLayers } from "./DxfViewer";

import apiClient from '../../../../services/api';

import MapRightSidebar from "./MapRightSidebar";
import MapTopSidebar from "./MapTopSidebar";
import EditBackground from "./EditBackground";
import { ProductsFixtures } from "./ProductsFixtures";

import mainFont from "../../../../fonts/RobotoCondensed-Regular.ttf";

// import testFile from '../../../../img/bratislava.dxf';

import DxfParserAPI from "./DxfParser";

// import AreaSelect from "./AreaSelect";

import { SelectFloorPlanLayer } from "./SelectFloorPlanLayer";

import GridLayer from "./layers/Grid";
import ZonesLayer from "./layers/Zones";
import ElementsLayer, { ELEMENTS_LAYER_ID } from "./layers/Elements";
import SelectionLayer from "./layers/Selection";
import FloorLayer from "./layers/Floor";
import RulerHelper from "./RulerHelper";

import { setSideBarIsOpened } from "../../../../store/actions/Store";

const fonts = [mainFont];

/**
 * Bring floor plan to the center of the viewport
 * @returns
 */
const reCenterMap = () => {
    store.dispatch(setZoomToElementId(ELEMENTS_LAYER_ID));
}

export default function Map() {
    const { floorPlanId } = useParams();

    const theme = useTheme();

    const panZoomRef = useRef(null);

    const isStoreEdit = useSelector((state) => state.store.isFloorEdit);
    const storeData = useSelector((state) => state.store.storeData);
    const unsavedModifications = useSelector((state) => state.store.unsavedModifications);
    const isMapReCentered = useSelector((state) => state.store.recenterMap);
    const floorMapLayerProperties = useSelector((state) => state.store.floorMapLayerProperties);
    const storeUpdated = useSelector((state) => state.store.storeUpdated);
    const zoomToElementId = useSelector((state) => state.store.zoomToElementId);
    //const historyStep = useSelector((state) => state.store.historyStep);
    //const [drawingCanceled, setDrawingCanceled] = useState(false);
    
    // const [scale, setScale] = useState(1);
    // const [tooltipScale, setTooltipScale] = useState(1);
    const [scaleStep, setScaleStep] = useState(0.01);
    const [panZoomState, setPanZoomState] = useState({ ox: 0, oy: 0, s: 1 });
    const [panZoomDisabled, setPanZoomDisabled] = useState(false); // TODO: move to global state
    
    const [dxfLayers, setDxfLayers] = useState([]);
    const [dxfLayersVisibility, setDxfLayersVisibility] = useState([]);
    const [dxfInfo, setDxfInfo] = useState({ size: { width: 1, height: 1 }, scale: 1 });
    const [dxfView, setDxfView] = useState({ x: 0, y: 0, z: 1, w: 0, h : 0 });
    const [dxfRelativeScale, setDxfRelativeScale] = useState(1);
    const [dxfOffset, setDxfOffset] = useState({ x: 0, y: 0 });


    //const [isSelectFloorPlanLayerDialogOpen, setIsSelectFloorPlanLayerDialogOpen] = useState(false);

	const canvasRef = useRef(null);

    const { t } = useTranslation();

    const st = storeData && storeData.floor_plan ? storeData.floor_plan : null; // store

    const stw = st ? st.width : 100; // store width
    const std = st ? st.depth : 100; // store depth

    const sts = st ? st.scale : 100; // store scale

    // const gw = st ? st.grid_width : 100; // grid width
    // const gd = st ? st.grid_depth : 100; // grid height

    const tx = storeData && storeData.texture ? storeData.texture : null; // texture
    const txw = tx ? tx.width : 1; // texture width
    const txd = tx ? tx.depth : 1; // texture depth
    const txpx = tx ? tx.positionX : 0; // texture pos x
    const txpy = tx ? tx.positionY : 0; // texture pos y

    const txo = storeData && storeData.dxf ? storeData.dxf : null; // texture orig
    const isDXF = txo && txo.endsWith('.dxf');

    const [contextMenu, setContextMenu] = React.useState(null);

    const beforeUnloadCallback = () => {
        return unsavedModifications ? t('You will loose all changes you made, continue?') : null;
    }

    useEffect(() => {
        window.onbeforeunload = () => beforeUnloadCallback()
    }, [unsavedModifications]); // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => {
        const { size, scale } = dxfInfo;
        const relativeScale = (size.width * scale) / stw;
        
        setDxfRelativeScale(6.038885917489634);
    }, [dxfInfo, stw]);

    const updateDxfView = (state) => {
        if(!panZoomRef.current) {
            return;
        }

        const { ox, oy, s } = state;
        const pz = panZoomRef.current.instance; // panzoom
        const c = pz.wrapperComponent; // panzoom container
        const dc = pz.contentComponent; // panzoom drag container
        const w = c.offsetWidth || 1; // container width
        const h = c.offsetHeight || 1; // container height
        const dcw = dc.offsetWidth || 1; // drag container width
        const dch = dc.offsetHeight || 1; // drag container height
        const z = dcw * s / w; // zoom (relative scale)
        const x = ((ox + dcw*s/2) / w - 0.5) / z; // normalized x offset
        const y = ((oy + dch*s/2) / h - 0.5) / z; // normalized y offset
        const fixed = { w, h, z, x, y };

        setDxfView(fixed);
    };

    useEffect(() => {
        updateDxfView(panZoomState);
    }, [stw, std, sts]); // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => {
        setDxfOffset({ x: Number(txpx) * sts, y: Number(txpy) * sts });
    }, [txpx, txpy]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        window.addEventListener('resize', reCenterMap);
        return () => window.removeEventListener('resize', reCenterMap);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isMapReCentered) {
            reCenterMap();
            store.dispatch(setRecenterMap(false));
        }
    }, [isMapReCentered]);

    useEffect(() => {
        if (!floorMapLayerProperties || !storeData) {
            return;
        }

        const width = storeData.floor_plan.width;
        const depth = storeData.floor_plan.depth;
        const positionX = storeData.texture.positionX;
        const positionY = storeData.texture.positionY;

        const clone = storeData;

        if(width !== floorMapLayerProperties.width) {
            clone.floor_plan.width = floorMapLayerProperties.width;
        }

        if (depth !== floorMapLayerProperties.depth) {
            clone.floor_plan.depth = floorMapLayerProperties.depth;
        }

        if (positionX !== floorMapLayerProperties.offsetX) {
            clone.texture.positionX = floorMapLayerProperties.offsetX;
        }

        if (positionY !== floorMapLayerProperties.offsetY) {
            clone.texture.positionY = floorMapLayerProperties.offsetY;
        }

        store.dispatch(setStoreData(clone));
        store.dispatch(setIsSceneUpdated(true));
        
        updateDxfView({ ox: floorMapLayerProperties.offsetX, oy: floorMapLayerProperties.offsetY });
    }, [floorMapLayerProperties]); // eslint-disable-line react-hooks/exhaustive-deps

    const mounted = useRef(false);

    useEffect(() => {
        if (window.innerWidth < 650) {
            store.dispatch(setSideBarIsOpened(false));
            reCenterMap();
        }

        mounted.current = true;
        return () => {
            const href = window.location.href;
            const items = href.split('/');
            if (items.splice(-1).pop() !== 'preview') {
                mounted.current = false;
                store.dispatch(setStoreData(null));
                store.dispatch(setSelectedStoreElement(null));
            }
        };
    }, []);

    /**
     * PanZoom on transformed callback
     * 
     * @param {*} state 
     */
    const onTransformed = (state) => {
        setPanZoomState(state);
        updateDxfView(state); // TODO: check to remove
    };

    /**
     * DXF file loaded callback
     * 
     * @param {array} layers
     * @param {array} layers
     * @param {array} layers
     */
    const onDxfLoaded = ({ layers, size, scale }) => {
        setDxfLayers(layers);
        setDxfInfo({ size, scale });
        reCenterMap();
    };

    /**
     * Show selected project elements table (products or fixtures)
     * 
     * @param {string} type 
     */
    const showElements = (type) => {
        store.dispatch(showProjectElements(type));
        setContextMenu(null);
    };

    /**
     * Initialize save floor plan base layer listener
     * 
     * @param {string} name 
     */
    const onSaveFloorPlanLayer = (name) => {
        const postData = {
            id: storeData.floor_plan.floor_plan_id,
            layer: name
        };

        apiClient.post("/api/easy-floor/set-floor-plan-layer", postData).then(response => {
            window.location.reload(false);
            /*
            let clone = storeData
            clone.floor_plan.floor_plan_layer = response.data.floor_plan.floor_plan_layer;
            store.dispatch(toggleMessageOpen("Floor plan layer changed successfully", "success"));
            store.dispatch(setStoreData(clone));
            store.dispatch(setIsSceneUpdated(true));
            //store.dispatch(setRefreshDxfLayerParam(true));
            */
        })
        .catch(error => {
            store.dispatch(toggleMessageOpen(error.response.data.message, "error"))
        })
    }
    
    /**
     * Initialize scale step input change listener
     * 
     * @param {KeyboardEvent} event 
     */
    const handleScaleStepInputChange = (event) => {
        setScaleStep(event.target.value === '' ? 0 : Number(event.target.value));
    }

    useEffect(() => {
        const zoomToElement = panZoomRef?.current?.zoomToElement;
        if (zoomToElement && zoomToElementId !== null) {
            zoomToElement(zoomToElementId);
            store.dispatch(setZoomToElementId(null));
        }
    }, [zoomToElementId]);

    useEffect(() => {
        reCenterMap();
    }, [])

    useEffect(() => {
        if(!isStoreEdit) {
            reCenterMap();
        }
    }, [isStoreEdit])

    return (
        <div id="outer-wrapper" style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <SelectFloorPlanLayer
                layers={dxfLayers}
                onSave={onSaveFloorPlanLayer}
            />
            {!isStoreEdit ?
                <div id="container-wrapper" style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <RulerHelper panZoomState={panZoomState} stw={stw} std={std} />
                    <div style={{position: 'absolute', top: '35px', left: '35px', width: 'calc(100% - 35px)'}}>
                        <div
                            id="container"
                            style={{
                                width: '100%',
                                height: '100%',
                                backgroundColor: '#afb8c0',
                                overflow: 'hidden',
                                position: 'relative'
                            }}
                        >

                            {/*}
                            <AreaSelect 
                                isEnabled={isAreaSelectEnabled}
                                areaSelectEnable={areaSelectEnable}
                                areaSelectDisable={areaSelectDisable}
                                scale={scale}
                            />
                            {*/}

                            {isDXF && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%'
                                    }}
                                >
                                    <DXF
                                        url={txo}
                                        fonts={fonts}
                                        x={dxfView.x}
                                        y={dxfView.y}
                                        zoom={dxfView.z}
                                        width={dxfView.w}
                                        height={dxfView.h}
                                        scale={dxfRelativeScale}
                                        ox={dxfOffset.x}
                                        oy={dxfOffset.y}
                                        debug={IS_DEV}
                                        onLoaded={onDxfLoaded}
                                        layers={dxfLayersVisibility}
                                    />
                                    <DxfParserAPI
                                        layer={storeData && storeData.floor_plan ? storeData.floor_plan.floor_plan_layer : ''}
                                        url={txo}
                                        storeUpdated={storeUpdated}
                                    />
                                </div>
                            )}

                            <TransformWrapper
                                initialScale={0.01}
                                minScale={0.01}
                                //maxScale={0.01}
                                wheel={{ step: scaleStep }}
                                ref={panZoomRef}
                                centerOnInit={true}
                                velocityAnimation={{disabled: true}}
                                alignmentAnimation={{disabled: true}}
                                zoomAnimation={{disabled: true}}
                                centerContent={false}
                                disabled={panZoomDisabled}
                                onTransformed={(panZoomRef, props) => {
                                    // setScale(props.scale);
                                    // setTooltipScale(props.scale);
                                    onTransformed({ ox: props.positionX, oy: props.positionY, s: props.scale });
                                }}
                                attr={{
                                    'style': {backgroundColor: '#000'}
                                }}
                            >
                                {(utils) => (
                                    <TransformComponent>
                                        <canvas
                                            ref={canvasRef}
                                            style={{
                                                position: 'absolute',
                                                top: (txpy * sts) + 'px',
                                                left: (txpx * sts) + 'px',
                                                width: txw  + 'px',
                                                height: txd + 'px',
                                            }}
                                        />
                                        {!isDXF && <FloorLayer />}
                                        <GridLayer scale={panZoomState.s} />
                                        <ZonesLayer
                                            scale={panZoomState.s}
                                        />
                                        <SelectionLayer
                                            scale={panZoomState.s}
                                            onSelectStart={() => setPanZoomDisabled(true)}
                                            onSelectEnd={() => setPanZoomDisabled(false)}
                                        />
                                        <ElementsLayer
                                            scale={panZoomState.s}
                                            floorPlanId={floorPlanId}
                                            setContextMenu={setContextMenu}
                                        />
                                    </TransformComponent>
                                )}
                            </TransformWrapper>

                            <div
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    right: '20px',
                                    zIndex: 10,
                                    transform: 'translateY(-50%)',
                                    height: window.innerHeight < 550 ? '70%' : 'unset',
                                    overflowY: window.innerHeight < 550 ? 'auto' : 'none',
                                }}
                            >
                                <MapRightSidebar />
                            </div>

                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: '20px',
                                    left: '20px',
                                    zIndex: 10,
                                    transform: 'translateY(-50%)'
                                }}
                            >
                                <ScaleStepSlider
                                    scaleStep={scaleStep}
                                    //handleScaleStepSliderChange={handleScaleStepSliderChange}
                                    handleScaleStepInputChange={handleScaleStepInputChange}
                                />
                            </div>

                            <Paper
                                sx={{
                                    padding: 0.5,
                                    color: '#388EB1',
                                    backgroundColor: theme.palette.background.default,
                                }}
                                style={{
                                    position: 'absolute',
                                    output: 'right',
                                    left: '20px',
                                    top: '20px',
                                    fontWeight: 'bold',
                                    zIndex: 15,
                                    display: (storeData ? 'none' : 'block')
                                }}
                            >
                                <Trans>Store is loading</Trans>...
                            </Paper>
                            
                            {isDXF && dxfLayers.length > 0 && (
                                <Paper
                                    elevation={8}
                                    style={{
                                        position: 'absolute',
                                        right: '20px',
                                        bottom: '60px',
                                        zIndex: 10001,
                                        display: (!isDXF ? 'none' : 'block')
                                    }}
                                >
                                    <DxfLayers
                                        layers={dxfLayers}
                                        onChanged={setDxfLayersVisibility}
                                        selected={storeData ? storeData.floor_plan.floor_plan_layer : null}
                                    />
                                </Paper>
                            )}

                            {/*<Scale scale={scale} t={t} />*/}

                            <Menu
                                open={contextMenu !== null}
                                anchorReference="anchorPosition"
                                anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                                onClose={() => setContextMenu(null)}
                            >
                                <MenuItem onClick={() => showElements('products')}>
                                    <Trans>View Products</Trans>
                                </MenuItem>
                                <MenuItem onClick={() => showElements('fixtures')}>
                                    <Trans>View Fixtures</Trans>
                                </MenuItem>
                            </Menu>

                            <ProductsFixtures />

                        </div>
                    </div>
                    {/*}
                    <MyToolTip
                        rulerText={rulerText}
                        rulerPositionTop={rulerPositionTop}
                        rulerPositionRight={rulerPositionRight}
                        tooltipScale={tooltipScale}
                        selected={selected}
                    />
                    {*/}
                </div>
            : <EditBackground layers={dxfLayers} /> }
        </div>
    );
}

/**
 * Scale step input
 * 
 * @param {Number} scaleStep 
 * @param {} scaleStep 
 * @param {KeyboardEvent} scaleStep 
 * @returns 
 */
const ScaleStepSlider = ({scaleStep, handleScaleStepSliderChange, handleScaleStepInputChange}) => {
    return (
        <Box sx={{ width: 250 }}>
            {/*}
            <Grid container spacing={2} alignItems="center">
                <Grid item xs>
                    <Slider
                        min={0.001}
                        max={1}
                        step={0.001}
                        value={typeof scaleStep === 'number' ? scaleStep : 0}
                        onChange={handleScaleStepSliderChange}
                        aria-label="Default"
                        valueLabelDisplay="auto"
                    />
                </Grid>
                <Grid item>
                {*/}
                    <Input
                        value={scaleStep}
                        size="small"
                        onChange={handleScaleStepInputChange}
                        style={{
                            width: '72px',
                            color: '#fff',
                            background: '#27324D',
                            borderRadius: '12px',
                            padding: '5px',
                            overflow: 'hidden',
                            outline: 'none'
                        }}
                        inputProps={{
                            step: 0.001,
                            min: 0.001,
                            max: 1,
                            type: 'number',
                        }}
                    />
                    {/*}
                </Grid>
            </Grid>
            {*/}
        </Box>
    )
}